import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName?: string;
  phone?: string;
  phoneOffice?: string;
  company?: string;
  address?: string;
  provinceName?: string;
  regencyName?: string;
  districtName?: string;
  villageName?: string;
  role: 'admin' | 'reseller' | 'agent';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  resellerId?: string;
  subscriptionType?: 'trial' | 'subscribe';
  servicePackage?: 'starter' | 'starter-plus' | 'basic' | 'premium';
  emailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

export interface LoginResponse {
  success: boolean;
  data: {
    userId: string;
    email: string;
    firstName: string;
    requireOtp: boolean;
    message: string;
  };
}

export interface VerifyOtpResponse {
  success: boolean;
  data: {
    user: User;
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.loadUserFromStorage();
  }

  private loadUserFromStorage(): void {
    const userData = localStorage.getItem('currentUser');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        this.currentUserSubject.next(user);
      } catch {
        this.clearStorage();
      }
    }
  }

  private clearStorage(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('pendingEmail');
  }

  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  get isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  // Step 1: Login - send credentials, receive OTP via email
  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, { email, password }).pipe(
      tap(response => {
        if (response.success && response.data.requireOtp) {
          // Store email for OTP verification
          localStorage.setItem('pendingEmail', email);
        }
      }),
      catchError(this.handleError)
    );
  }

  // Step 2: Verify OTP and complete login
  verifyOtp(email: string, code: string): Observable<VerifyOtpResponse> {
    return this.http.post<VerifyOtpResponse>(`${this.apiUrl}/auth/verify-otp`, { email, code }).pipe(
      tap(response => {
        if (response.success) {
          localStorage.setItem('accessToken', response.data.accessToken);
          localStorage.setItem('refreshToken', response.data.refreshToken);
          localStorage.setItem('currentUser', JSON.stringify(response.data.user));
          localStorage.removeItem('pendingEmail');
          this.currentUserSubject.next(response.data.user);
        }
      }),
      catchError(this.handleError)
    );
  }

  // Resend OTP
  resendOtp(email: string, type: 'login' | 'reset_password' = 'login'): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiUrl}/auth/resend-otp`, { email, type }).pipe(
      catchError(this.handleError)
    );
  }

  // Logout
  logout(): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiUrl}/auth/logout`, {}).pipe(
      tap(() => {
        this.clearStorage();
        this.currentUserSubject.next(null);
        this.router.navigate(['/auth/login']);
      }),
      catchError(error => {
        // Even if API fails, clear local storage
        this.clearStorage();
        this.currentUserSubject.next(null);
        this.router.navigate(['/auth/login']);
        return throwError(() => error);
      })
    );
  }

  // Force logout (no API call)
  forceLogout(): void {
    this.clearStorage();
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  // Logout all sessions
  logoutAll(): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiUrl}/auth/logout-all`, {}).pipe(
      tap(() => {
        this.clearStorage();
        this.currentUserSubject.next(null);
        this.router.navigate(['/auth/login']);
      }),
      catchError(this.handleError)
    );
  }

  // Get current user profile
  getProfile(): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(`${this.apiUrl}/auth/me`).pipe(
      tap(response => {
        if (response.success) {
          localStorage.setItem('currentUser', JSON.stringify(response.data));
          this.currentUserSubject.next(response.data);
        }
      }),
      catchError(this.handleError)
    );
  }

  // Refresh token
  refreshToken(): Observable<ApiResponse<{ accessToken: string; refreshToken: string; expiresAt: string }>> {
    const refreshToken = this.getRefreshToken();
    return this.http.post<ApiResponse<{ accessToken: string; refreshToken: string; expiresAt: string }>>(
      `${this.apiUrl}/auth/refresh-token`,
      { refreshToken }
    ).pipe(
      tap(response => {
        if (response.success) {
          localStorage.setItem('accessToken', response.data.accessToken);
          localStorage.setItem('refreshToken', response.data.refreshToken);
        }
      }),
      catchError(this.handleError)
    );
  }

  // Change password
  changePassword(currentPassword: string, newPassword: string): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiUrl}/auth/change-password`, {
      currentPassword,
      newPassword
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Request password reset
  forgotPassword(email: string): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiUrl}/auth/forgot-password`, { email }).pipe(
      tap(() => {
        localStorage.setItem('pendingEmail', email);
      }),
      catchError(this.handleError)
    );
  }

  // Reset password with OTP
  resetPassword(email: string, code: string, newPassword: string): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(`${this.apiUrl}/auth/reset-password`, {
      email,
      code,
      newPassword
    }).pipe(
      tap(() => {
        localStorage.removeItem('pendingEmail');
      }),
      catchError(this.handleError)
    );
  }

  // Get active sessions
  getSessions(): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/auth/sessions`).pipe(
      catchError(this.handleError)
    );
  }

  // Get login history
  getLoginHistory(limit: number = 10): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/auth/login-history?limit=${limit}`).pipe(
      catchError(this.handleError)
    );
  }

  // Get pending email for OTP
  getPendingEmail(): string | null {
    return localStorage.getItem('pendingEmail');
  }

  // Navigate based on role
  navigateByRole(): void {
    const user = this.currentUser;
    if (!user) {
      this.router.navigate(['/auth/login']);
      return;
    }

    // Store role for navigation component
    localStorage.setItem('userRole', user.role);

    switch (user.role) {
      case 'admin':
        this.router.navigate(['/admin/dashboard']);
        break;
      case 'reseller':
        this.router.navigate(['/reseller/dashboard']);
        break;
      case 'agent':
        this.router.navigate(['/agent/dashboard']);
        break;
      default:
        this.router.navigate(['/auth/login']);
    }
  }

  // Get dashboard URL based on role
  getDashboardUrl(): string {
    const user = this.currentUser;
    if (!user) return '/auth/login';

    switch (user.role) {
      case 'admin':
        return '/admin/dashboard';
      case 'reseller':
        return '/reseller/dashboard';
      case 'agent':
        return '/agent/dashboard';
      default:
        return '/auth/login';
    }
  }

  private handleError(error: any) {
    let errorMessage = 'Terjadi kesalahan';
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    return throwError(() => new Error(errorMessage));
  }
}
